import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { ECompareMode } from 'ts/commons/code/compare/CompareCodeComponent';
import { safeLinkTo } from 'ts/commons/links/LinkTo';
import { Route as CodeCompareRoute } from 'ts/routes/_perspectives/compare/code';
import { Route as CoverageCompareRoute } from 'ts/routes/_perspectives/compare/coverage.$projectId';
import { Route as SpecItemCompareRoute } from 'ts/routes/_perspectives/compare/spec-item-compare.$projectId';
import type { CommitDescriptor } from 'typedefs/CommitDescriptor';

/** The name of the parameter that stores the compare mode in the navigation hash. */
export const COMPARE_MODE_PARAMETER = 'compare-mode';

/** Describes one side of the compare view link. */
type CompareSideWithProject = {
	project: string;
	uniformPath: string;
	commit: UnresolvedCommitDescriptor | CommitDescriptor;
	startLine?: number;
	endLine?: number;
};

/** Describes a specific file at a particular commit. Used as a parameter for file comparison. */
export type FileAtCommit = {
	uniformPath: string;
	commit: UnresolvedCommitDescriptor | CommitDescriptor;
	startLine?: number;
};

type CompareOptions = {
	/** Optional boolean to denote if it's an inconsistent clone. */
	isInconsistentClone?: boolean;
	compareMode?: ECompareMode;
	showCoverage?: boolean;
};

/** Represents a specific range of lines in a file. */
export type LineRange = {
	startLine: number;
	endLine: number;
};

/** Parameters used to build compare links. */
export type CompareLinkParameters = [CompareSideWithProject, CompareSideWithProject];

/** Returns a URL for comparing two locations within two files in the same project for a commit. */
export function compareFiles(
	project: string,
	leftSide: FileAtCommit,
	rightSide: FileAtCommit,
	options?: CompareOptions
): string;
export function compareFiles(
	project: string,
	leftSide: FileAtCommit & LineRange,
	rightSide: FileAtCommit & LineRange,
	options?: CompareOptions
): string;
export function compareFiles(
	project: string,
	leftSide: FileAtCommit & Partial<LineRange>,
	rightSide: FileAtCommit & Partial<LineRange>,
	options?: CompareOptions
): string {
	return comparePerspective(
		{
			project,
			...leftSide
		},
		{
			project,
			...rightSide
		},
		options
	);
}

/**
 * Returns a URL for comparing two files.
 *
 * @param leftParams
 * @param leftParams.project The source project.
 * @param leftParams.uniformPath The source uniform path.
 * @param leftParams.commit The source commit.
 * @param leftParams.startLine The source start line number.
 * @param leftParams.endLine The source end line number.
 * @param rightParams
 * @param rightParams.project The target project.
 * @param rightParams.uniformPath The target uniform path.
 * @param rightParams.commit The target commit.
 * @param rightParams.startLine The target start line number.
 * @param rightParams.endLine The target end line number.
 * @param options Additional compare view options that should be appended to the URL
 */
export function comparePerspective(
	leftParams: CompareSideWithProject,
	rightParams: CompareSideWithProject,
	options?: CompareOptions
) {
	const leftStartLine = leftParams.startLine;
	const leftEndLine = leftParams.endLine;
	const rightStartLine = rightParams.startLine;
	const rightEndLine = rightParams.endLine;

	return safeLinkTo({
		to: CodeCompareRoute.to,
		search: {
			'left-project': leftParams.project,
			'right-project': rightParams.project,
			'left-path': leftParams.uniformPath,
			'right-path': rightParams.uniformPath,
			'left-commit': UnresolvedCommitDescriptor.wrap(leftParams.commit),
			'right-commit': UnresolvedCommitDescriptor.wrap(rightParams.commit),
			...(leftStartLine && leftEndLine && rightStartLine && rightEndLine
				? // Opens the compare view for a diff of regions within the two files. Only fully defined regions are supported.
					{ region: `${leftStartLine}-${leftEndLine}:${rightStartLine}-${rightEndLine}` }
				: leftStartLine
					? // if regions to be compared are not fully defined, but left start line is known, use initial scroll feature of compare view.
						{ 'left-line': leftStartLine }
					: rightStartLine
						? // if regions to be compared are not fully defined, but right start line is known, use initial scroll feature of compare view.
							{ 'right-line': rightStartLine }
						: undefined),
			...(options?.isInconsistentClone ? { 'inconsistent-clone': options.isInconsistentClone } : undefined),
			...(options?.compareMode ? { [COMPARE_MODE_PARAMETER]: String(options.compareMode) } : undefined),
			...(options?.showCoverage ? { 'show-coverage': options.showCoverage } : undefined)
		}
	});
}

/** Information about a single Spec Item compare element */
export type SpecItemCompareInformation = {
	/** ID of the underlying item */
	issueId: string;
	/** ID of the finding */
	findingId: string;
};

/** Returns a URL for comparing spec items. */
export function compareSpecItems(
	projectId: string,
	left: SpecItemCompareInformation,
	right: SpecItemCompareInformation,
	commit: UnresolvedCommitDescriptor
) {
	return safeLinkTo({
		to: SpecItemCompareRoute.to,
		params: { projectId },
		search: {
			'left-compare': JSON.stringify(left),
			'right-compare': JSON.stringify(right),
			t: commit
		}
	});
}

/** Returns a URL for comparing coverage of changes. */
export function compareTestCoverage(
	projectId: string,
	mergeRequestId: string,
	originPath: string,
	path: string,
	commit: UnresolvedCommitDescriptor
) {
	return safeLinkTo({
		to: CoverageCompareRoute.to,
		params: { projectId },
		search: {
			'merge-request': mergeRequestId,
			'origin-path': originPath,
			path,
			t: commit,
			'show-coverage': true,
			[COMPARE_MODE_PARAMETER]: String(ECompareMode.LINE_BASED_IGNORE_WHITESPACE)
		}
	});
}
